(function () {
  if (window.EzupCustomizedBundleNative) return;

  const BUNDLE_ROOT_SELECTOR = "[data-customized-bundle]";
  const SLOT_SELECTOR = "[data-customized-bundle-slot]";
  const COMPONENT_SELECTOR = "[data-customized-bundle-component]";
  const MATRIX_SELECTOR = "[data-customized-bundle-selector]";
  const QUANTITY_SELECTOR = "[data-customized-bundle-quantity]";
  const ADD_TO_CART_SELECTOR = '[data-action="add-to-cart"]';

  function normalizeVariantId(value) {
    const text = String(value || "").trim();
    const match = text.match(/ProductVariant\/(\d+)$/);
    return match ? match[1] : text;
  }

  function readPositiveInteger(value, fallback) {
    const parsed = parseInt(String(value || ""), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  function readInteger(value, fallback) {
    const parsed = parseInt(String(value || ""), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function readMoneyCents(value, fallback = 0) {
    const parsed = Number.parseFloat(String(value ?? "").replace(/[^0-9.-]/g, ""));
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : fallback;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function parseConfig(root) {
    const script = root && root.querySelector("[data-customized-bundle-config]");
    if (!script) return null;

    try {
      const config = JSON.parse(script.textContent || "{}");
      if (config && config.type === "customized_bundle" && Array.isArray(config.slots)) {
        return config;
      }
    } catch (error) {
      console.error("Customized bundle: invalid config JSON", error);
    }

    return null;
  }

  function getSlotQuantity(slot) {
    const input = slot.querySelector(QUANTITY_SELECTOR);
    if (!input) return 0;
    return Math.max(0, readInteger(input.value, 0));
  }

  function sanitizeQuantityInput(value) {
    return String(value || "").replace(/[^\d]/g, "");
  }

  function setSlotQuantity(slot, nextQuantity) {
    const input = slot.querySelector(QUANTITY_SELECTOR);
    if (!input) return;

    const min = Math.max(0, readInteger(slot.dataset.minQuantity, 0));
    const max = Math.max(min, readInteger(slot.dataset.maxQuantity, 999));
    const mode = slot.dataset.quantityMode || "editable";
    const selectedComponent = getSelectedComponent(slot);
    const hasChoiceInput = Boolean(slot.querySelector('select, input[type="radio"][data-customized-bundle-component]'));
    const inactiveMultiChoice = !selectedComponent && hasChoiceInput;
    const fixedQuantity = Math.max(0, readInteger(slot.dataset.defaultQuantity, 1));
    const value = inactiveMultiChoice
      ? 0
      : mode === "fixed"
        ? fixedQuantity
        : clamp(Math.max(0, readInteger(nextQuantity, 0)), min, max);

    input.value = String(value);
    updateQuantityButtons(slot);
  }

  function commitSlotQuantity(slot) {
    const input = slot.querySelector(QUANTITY_SELECTOR);
    if (!input) return;

    const selectedComponent = getSelectedComponent(slot);
    const hasChoiceInput = Boolean(slot.querySelector('select, input[type="radio"][data-customized-bundle-component]'));
    const mode = slot.dataset.quantityMode || "editable";
    const min = Math.max(0, readInteger(slot.dataset.minQuantity, 0));
    const defaultQuantity = Math.max(0, readInteger(slot.dataset.defaultQuantity, 1));

    if (!selectedComponent && hasChoiceInput) {
      setSlotQuantity(slot, 0);
      return;
    }

    if (mode === "fixed") {
      setSlotQuantity(slot, defaultQuantity);
      return;
    }

    if (getSlotQuantity(slot) <= 0) {
      setSlotQuantity(slot, Math.max(min, defaultQuantity, 1));
      return;
    }

    setSlotQuantity(slot, getSlotQuantity(slot));
  }

  function commitAllSlotQuantities(root) {
    if (!root) return;
    root.querySelectorAll(SLOT_SELECTOR).forEach(commitSlotQuantity);
  }

  function updateQuantityButtons(slot) {
    const input = slot.querySelector(QUANTITY_SELECTOR);
    if (!input) return;

    const decrease = slot.querySelector("[data-customized-bundle-quantity-decrease]");
    const increase = slot.querySelector("[data-customized-bundle-quantity-increase]");
    const mode = slot.dataset.quantityMode || "editable";
    const selectedComponent = getSelectedComponent(slot);
    const hasChoiceInput = Boolean(slot.querySelector('select, input[type="radio"][data-customized-bundle-component]'));
    const inactiveMultiChoice = !selectedComponent && hasChoiceInput;
    const quantity = getSlotQuantity(slot);
    const min = Math.max(0, readInteger(slot.dataset.minQuantity, 0));
    const max = Math.max(min, readInteger(slot.dataset.maxQuantity, 999));
    const controlsDisabled = mode === "fixed" || inactiveMultiChoice;

    if (decrease) {
      decrease.disabled = controlsDisabled || quantity <= min;
    }
    if (increase) {
      increase.disabled = controlsDisabled || quantity >= max;
    }
    input.readOnly = controlsDisabled;
  }

  function getSelectedComponent(slot) {
    const radioInput = slot.querySelector('input[type="radio"][data-customized-bundle-component]');
    if (radioInput) {
      const checkedRadio = slot.querySelector('input[type="radio"][data-customized-bundle-component]:checked');
      if (!checkedRadio) return null;

      return {
        variantId: checkedRadio.value,
        title: cleanComponentTitle(checkedRadio.dataset.componentTitle || ""),
        price: checkedRadio.dataset.componentPrice || "0.00",
        priceCents: readInteger(checkedRadio.dataset.componentPriceCents, readMoneyCents(checkedRadio.dataset.componentPrice)),
      };
    }

    const componentInput = slot.querySelector(COMPONENT_SELECTOR);
    if (!componentInput) return null;

    if (componentInput.tagName === "SELECT") {
      const option = componentInput.selectedOptions && componentInput.selectedOptions[0];
      if (!componentInput.value || !option) return null;
      return {
        variantId: componentInput.value,
        title: cleanComponentTitle(option.dataset.componentTitle || option.textContent.trim()),
        price: option.dataset.componentPrice || "0.00",
        priceCents: readInteger(option.dataset.componentPriceCents, readMoneyCents(option.dataset.componentPrice)),
      };
    }

    if (!componentInput.value) return null;
    return {
      variantId: componentInput.value,
      title: cleanComponentTitle(componentInput.dataset.componentTitle || ""),
      price: componentInput.dataset.componentPrice || "0.00",
      priceCents: readInteger(componentInput.dataset.componentPriceCents, readMoneyCents(componentInput.dataset.componentPrice)),
    };
  }

  function cleanComponentTitle(value) {
    return String(value || "")
      .replace(/\s*-\s*Default Title\s*$/i, "")
      .replace(/\s*Default Title\s*$/i, "")
      .trim();
  }

  function clearSlotError(slot) {
    const error = slot.querySelector("[data-customized-bundle-error]");
    slot.querySelectorAll(`${COMPONENT_SELECTOR}, ${MATRIX_SELECTOR}, ${QUANTITY_SELECTOR}`).forEach((field) => {
      field.removeAttribute("aria-invalid");
      field.removeAttribute("aria-describedby");
    });
    if (error) {
      error.hidden = true;
      error.textContent = "";
    }
  }

  function setSlotError(slot, message, field) {
    const error = slot.querySelector("[data-customized-bundle-error]");
    if (!error) return;
    if (!error.id) {
      error.id = `customized-bundle-error-${slot.dataset.slotId || Math.random().toString(36).slice(2)}`;
    }
    error.textContent = message;
    error.hidden = false;
    if (field) {
      field.setAttribute("aria-invalid", "true");
      field.setAttribute("aria-describedby", error.id);
    }
  }

  function getFormError(root) {
    const scope = (root && root.closest("form")) || root;
    return scope && scope.querySelector("[data-customized-bundle-form-error]");
  }

  function clearFormError(root) {
    const error = getFormError(root);
    if (!error) return;
    error.hidden = true;
    error.textContent = "";
  }

  function setFormError(root, message) {
    const error = getFormError(root);
    if (!error) return;
    error.textContent = message || getRootMessage(root, "cartErrorMessage", "Unable to add this bundle to the cart. Please try again.");
    error.hidden = false;
  }

  function focusFormError(root) {
    const error = getFormError(root);
    if (!error || error.hidden) return;

    error.setAttribute("tabindex", "-1");
    if (error.scrollIntoView) {
      error.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    if (error.focus) {
      error.focus({ preventScroll: true });
    }
  }

  function getSelections(root) {
    const selections = [];
    const slots = Array.from(root.querySelectorAll(SLOT_SELECTOR));
    let focusTarget = null;

    for (const slot of slots) {
      clearSlotError(slot);

      const component = getSelectedComponent(slot);
      const required = slot.dataset.slotRequired === "true";
      const quantity = getSlotQuantity(slot);
      const label = slot.dataset.slotLabel || "Bundle option";

      if (!component) {
        if (required) {
          const field = Array.from(slot.querySelectorAll(MATRIX_SELECTOR)).find((selector) => !selector.value)
            || slot.querySelector(COMPONENT_SELECTOR);
          setSlotError(slot, getRootMessage(root, "requiredSelectionMessage", "This is a required field."), field);
          focusTarget = focusTarget || field;
        }
        continue;
      }

      if (quantity <= 0) {
        if (required) {
          const field = slot.querySelector(QUANTITY_SELECTOR);
          setSlotError(slot, getRootMessage(root, "requiredQuantityMessage", "This is a required field."), field);
          focusTarget = focusTarget || field;
        }
        continue;
      }

      selections.push({
        slotId: slot.dataset.slotId || "",
        label,
        quantity,
        includedQuantity: Math.max(0, readInteger(slot.dataset.defaultQuantity, 1)),
        includedComponentPriceCents: Math.max(0, readInteger(slot.dataset.includedComponentPriceCents, 0)),
        component,
      });
    }

    if (focusTarget) {
      return { ok: false, focusTarget };
    }

    return { ok: true, selections };
  }

  function getCurrentSelections(root) {
    return Array.from(root.querySelectorAll(SLOT_SELECTOR))
      .map((slot) => {
        const component = getSelectedComponent(slot);
        const quantity = getSlotQuantity(slot);

        if (!component || quantity <= 0) return null;

        return {
          slotId: slot.dataset.slotId || "",
          label: slot.dataset.slotLabel || "Bundle option",
          quantity,
          includedQuantity: Math.max(0, readInteger(slot.dataset.defaultQuantity, 1)),
          includedComponentPriceCents: Math.max(0, readInteger(slot.dataset.includedComponentPriceCents, 0)),
          component,
        };
      })
      .filter(Boolean);
  }

  function getRootMessage(root, key, fallback) {
    const value = root && root.dataset ? root.dataset[key] : "";
    return value || fallback;
  }

  function getFormProperties(form) {
    const properties = {};
    const formData = new FormData(form);

    formData.forEach((value, key) => {
      const match = String(key).match(/^properties\[(.+)\]$/);
      if (!match || value == null || value === "") return;
      properties[match[1]] = String(value);
    });

    return properties;
  }

  function getBundleQuantity(form) {
    const formData = new FormData(form);
    return readPositiveInteger(formData.get("quantity"), 1);
  }

  function getParentVariantId(form, config) {
    const formData = new FormData(form);
    return normalizeVariantId(formData.get("id") || (config && config.parentVariantId));
  }

  function usesComponentCartItems(root) {
    return root && root.dataset.customizedBundleCartAddMode === "component_items";
  }

  function getBundlePricing(form, root, selections) {
    const bundleQuantity = getBundleQuantity(form);
    const fallbackBasePriceCents = readInteger(root.dataset.parentPriceCents, 0);
    const basePriceCents = readInteger(root.dataset.bundleBasePriceCents, fallbackBasePriceCents);
    const selectedPriceCents = selections.reduce((total, selection) => {
      const componentPriceCents = readInteger(selection.component.priceCents, readMoneyCents(selection.component.price));
      const includedQuantity = Math.max(0, readInteger(selection.includedQuantity, 1));
      const includedComponentPriceCents = Math.max(0, readInteger(selection.includedComponentPriceCents, 0));
      const includedUnits = Math.min(selection.quantity, includedQuantity);
      const extraQuantity = Math.max(0, selection.quantity - includedQuantity);
      const selectionDeltaCents = includedComponentPriceCents > 0
        ? (componentPriceCents - includedComponentPriceCents) * includedUnits
        : 0;
      return total + selectionDeltaCents + componentPriceCents * extraQuantity;
    }, 0);
    const unitPriceCents = Math.max(0, basePriceCents + selectedPriceCents);

    return {
      bundleQuantity,
      basePriceCents,
      unitPriceCents,
      linePriceCents: unitPriceCents * bundleQuantity,
    };
  }

  function buildCartItems(form, root, config, selections) {
    if (usesComponentCartItems(root)) {
      return buildComponentCartItems(form, selections);
    }

    const parentVariantId = getParentVariantId(form, config);
    const pricing = getBundlePricing(form, root, selections);
    const selectionPayload = selections.map((selection) => ({
      s: selection.slotId,
      v: compactVariantId(selection.component.variantId),
      q: selection.quantity,
    }));
    const displaySelectionProperties = selections.reduce((properties, selection) => {
      const label = selection.label || selection.slotId || "Bundle option";
      properties[`_ezup_display_${label}`] = `${selection.quantity} x ${selection.component.title}${formatComponentPrice(selection.component.priceCents)}`;
      return properties;
    }, {});
    const formProperties = getFormProperties(form);
    delete formProperties.SKU;

    const parentProperties = Object.assign({}, formProperties, displaySelectionProperties, {
      _ezup_bundle_type: "customized",
      _ezup_customized_bundle: "true",
      _ezup_customized_bundle_selection: JSON.stringify(selectionPayload),
      _ezup_customized_bundle_unit_price: formatPlainMoney(pricing.unitPriceCents),
      _ezup_customized_bundle_unit_price_cents: String(pricing.unitPriceCents),
      _ezup_customized_bundle_line_price_cents: String(pricing.linePriceCents),
    });
    const parentItem = {
      id: parentVariantId,
      quantity: pricing.bundleQuantity,
      properties: parentProperties,
    };
    const sellingPlanInput = new FormData(form).get("selling_plan");

    if (sellingPlanInput) {
      parentItem.selling_plan = String(sellingPlanInput);
    }

    return [parentItem];
  }

  function buildComponentCartItems(form, selections) {
    const bundleQuantity = getBundleQuantity(form);

    return selections.map((selection) => ({
      id: compactVariantId(selection.component.variantId),
      quantity: Math.max(1, selection.quantity) * bundleQuantity,
    }));
  }

  function getCartUrl() {
    const cartUrl = window.routes && window.routes.cartUrl ? window.routes.cartUrl : "/cart";
    return `${cartUrl.replace(/\/$/, "")}.js`;
  }

  function getCartLimitForItem(cartItem) {
    const maxValues = [];
    const quantityRuleMax = cartItem && cartItem.quantity_rule ? readInteger(cartItem.quantity_rule.max, 0) : 0;
    const inventoryQuantity = readInteger(cartItem && cartItem.variant_inventory_quantity, 0);
    const inventoryPolicy = String((cartItem && cartItem.variant_inventory_policy) || "").toLowerCase();

    if (quantityRuleMax > 0) {
      maxValues.push(quantityRuleMax);
    }

    if (inventoryQuantity > 0 && inventoryPolicy !== "continue") {
      maxValues.push(inventoryQuantity);
    }

    if (!maxValues.length) {
      return null;
    }

    return Math.min.apply(Math, maxValues);
  }

  function groupRequestedCartItems(items) {
    return items.reduce((grouped, item) => {
      const id = compactVariantId(item && item.id);
      const quantity = readPositiveInteger(item && item.quantity, 1);

      if (!id) {
        return grouped;
      }

      grouped[id] = (grouped[id] || 0) + quantity;
      return grouped;
    }, {});
  }

  function checkCartLimitsBeforeAdd(form, root, selections) {
    const config = parseConfig(root);
    const items = buildCartItems(form, root, config, selections);
    const requestedByVariantId = groupRequestedCartItems(items);
    const requestedVariantIds = Object.keys(requestedByVariantId);

    if (!requestedVariantIds.length || !window.fetch) {
      return Promise.resolve(true);
    }

    return fetch(getCartUrl(), {
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => {
        if (!response.ok) {
          return true;
        }

        return response.json().then((cart) => {
          const cartItems = Array.isArray(cart && cart.items) ? cart.items : [];
          const blockedItem = cartItems.find((cartItem) => {
            const variantId = compactVariantId(cartItem.variant_id || cartItem.id);
            const requestedQuantity = requestedByVariantId[variantId] || 0;
            const existingQuantity = readInteger(cartItem.quantity, 0);
            const maxQuantity = getCartLimitForItem(cartItem);

            return requestedQuantity > 0 && maxQuantity != null && existingQuantity + requestedQuantity > maxQuantity;
          });

          if (!blockedItem) {
            return true;
          }

          setFormError(root, "The maximum quantity of this item is already in your cart.");
          focusFormError(root);
          return false;
        });
      })
      .catch(() => true);
  }

  function formatComponentPrice(priceCents) {
    const amountCents = readInteger(priceCents, 0);
    if (amountCents <= 0) return "";
    return ` ${formatPlainMoney(amountCents)}`;
  }

  function compactVariantId(variantId) {
    const id = String(variantId || "").trim();
    const match = id.match(/ProductVariant\/(\d+)$/);
    return match ? match[1] : id;
  }

  function formatMoney(cents) {
    const amountCents = Math.round(Number(cents) || 0);
    const currencyFormat = window.theme && window.theme.currencyCodeEnabled
      ? window.theme.moneyWithCurrencyFormat
      : window.theme && window.theme.moneyFormat;

    if (window.Currency && typeof window.Currency.formatMoney === "function" && currencyFormat) {
      return window.Currency.formatMoney(amountCents, currencyFormat);
    }

    return `$${(amountCents / 100).toFixed(2)}`;
  }

  function formatPlainMoney(cents) {
    const container = document.createElement("span");
    container.innerHTML = formatMoney(cents);
    const text = container.textContent.replace(/\s+/g, " ").trim();
    return text || `$${((Math.round(Number(cents) || 0)) / 100).toFixed(2)}`;
  }

  function buildPriceHtml(priceElement, amountCents) {
    const label = priceElement.querySelector(".total-price");
    const labelHtml = label ? label.outerHTML : "";
    return `${labelHtml}<span class="price"><span class="visually-hidden">${(window.languages && window.languages.productSalePrice) || "Price"}</span>${formatMoney(amountCents)}</span>`;
  }

  function updateDisplayedPrices(root) {
    if (!root) return;

    const form = root.closest('form[action*="/cart/add"]');
    if (!form) return;

    const pricing = getBundlePricing(form, root, getCurrentSelections(root));
    const productSection = root.closest('[data-section-type="product"]') || document;
    const mainPrice = form.querySelector(".product-unique-price.main-price");

    if (mainPrice && root.dataset.customizedBundleStaticMainPrice !== "true") {
      mainPrice.innerHTML = buildPriceHtml(mainPrice, pricing.unitPriceCents);
      mainPrice.style.display = "";
    }

    productSection.querySelectorAll("sticky-atc .product-unique-price, .sticky-atc .product-unique-price").forEach((stickyPrice) => {
      stickyPrice.innerHTML = buildPriceHtml(stickyPrice, pricing.linePriceCents);
      stickyPrice.style.display = "";
    });

    root.dispatchEvent(new CustomEvent("customized-bundle:price-change", {
      bubbles: true,
      detail: pricing,
    }));
  }

  function getAddUrl() {
    const addUrl = window.routes && window.routes.cartAddUrl
      ? window.routes.cartAddUrl
      : "/cart/add";
    return /\.js(?:[?#]|$)/.test(addUrl) ? addUrl : `${addUrl}.js`;
  }

  function readCartAddError(response) {
    return response.text().then((text) => {
      if (!text) return "";

      try {
        const data = JSON.parse(text);
        return data.description || data.message || data.error || "";
      } catch (error) {
        return text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      }
    });
  }

  function getMirroredAddToCartButtons(form, button) {
    const buttons = [];
    const addButton = (candidate) => {
      if (candidate && !buttons.includes(candidate)) {
        buttons.push(candidate);
      }
    };

    addButton(button);

    const productSection =
      (form && form.closest('[data-section-type="product"]')) ||
      (button && button.closest('[data-section-type="product"]')) ||
      (button && button.closest(".shopify-section") && button.closest(".shopify-section").querySelector('[data-section-type="product"]'));

    if (form) {
      form.querySelectorAll(ADD_TO_CART_SELECTOR).forEach(addButton);
    }

    if (productSection) {
      productSection
        .querySelectorAll("sticky-atc .product-form__add-button:not(.sticky-customize-it), .sticky-atc .product-form__add-button:not(.sticky-customize-it)")
        .forEach(addButton);
    }

    return buttons;
  }

  function getButtonOriginalLabel(button) {
    return button.getAttribute("data-atc-original-label") || button.innerHTML;
  }

  function setButtonLoading(button, isLoading, form) {
    if (!button) return;

    getMirroredAddToCartButtons(form, button).forEach((mirroredButton) => {
      if (isLoading) {
        if (!mirroredButton.hasAttribute("data-customized-bundle-original-label")) {
          mirroredButton.setAttribute("data-customized-bundle-original-label", getButtonOriginalLabel(mirroredButton));
        }
        mirroredButton.disabled = true;
        mirroredButton.innerHTML = (window.languages && window.languages.productFormAdding) || "ADDING...";
        return;
      }

      mirroredButton.disabled = false;
      if (mirroredButton.hasAttribute("data-customized-bundle-original-label")) {
        mirroredButton.innerHTML = mirroredButton.getAttribute("data-customized-bundle-original-label");
        mirroredButton.removeAttribute("data-customized-bundle-original-label");
      }
    });
  }

  function notifyAdded(form, button, quantity) {
    const productSection = form.closest('[data-section-type="product"]') || form;
    productSection.dispatchEvent(
      new CustomEvent("product:added", {
        bubbles: true,
        detail: {
          button,
          variant: null,
          quantity,
        },
      })
    );
  }

  function notifyLoadingEnd() {
    document.dispatchEvent(new CustomEvent("theme:loading:end"));
  }

  function shouldRedirectToCartAfterAdd() {
    return document.documentElement.getAttribute("data-redirect-to-cart-after-add") === "true";
  }

  function clearRedirectToCartAfterAdd() {
    document.documentElement.removeAttribute("data-redirect-to-cart-after-add");
  }

  function redirectToCart() {
    const cartUrl = window.routes && window.routes.cartUrl ? window.routes.cartUrl : "/cart";
    window.location.href = cartUrl;
  }

  function closeUploadLogoDialog() {
    const dialog = document.querySelector("upload-logo-dialog .dialog-overlay.active");
    const root = dialog && dialog.closest ? dialog.closest("upload-logo-dialog") : null;

    if (root && typeof root.closeDialog === "function") {
      root.closeDialog();
      return;
    }

    if (root && typeof root.close === "function") {
      root.close();
      return;
    }

    if (dialog) {
      dialog.classList.remove("active");
      document.documentElement.classList.remove("is-locked", "is-upload-logo-dialog-open");
      document.body.classList.remove("is-locked", "is-upload-logo-dialog-open");
    }
  }

  function validateCustomizedBundleForm(form) {
    const root = form && form.querySelector(BUNDLE_ROOT_SELECTOR);
    if (!root) return true;

    clearFormError(root);
    commitAllSlotQuantities(root);

    const selectionResult = getSelections(root);
    if (selectionResult.ok) {
      return true;
    }

    if (selectionResult.focusTarget && selectionResult.focusTarget.focus) {
      selectionResult.focusTarget.focus();
    }

    clearRedirectToCartAfterAdd();
    return false;
  }

  function validateCustomizedBundleFormForDialog(form) {
    const root = form && form.querySelector(BUNDLE_ROOT_SELECTOR);
    if (!root) return Promise.resolve(true);

    if (!validateCustomizedBundleForm(form)) {
      return Promise.resolve(false);
    }

    const selectionResult = getSelections(root);
    if (!selectionResult.ok) {
      return Promise.resolve(false);
    }

    return checkCartLimitsBeforeAdd(form, root, selectionResult.selections);
  }

  function submitCustomizedBundle(form, button) {
    const root = form && form.querySelector(BUNDLE_ROOT_SELECTOR);
    if (!root) return;

    const config = parseConfig(root);
    clearFormError(root);
    commitAllSlotQuantities(root);
    const selectionResult = getSelections(root);
    if (!selectionResult.ok) {
      if (selectionResult.focusTarget && selectionResult.focusTarget.focus) {
        selectionResult.focusTarget.focus();
      }
      clearRedirectToCartAfterAdd();
      notifyLoadingEnd();
      return;
    }

    const items = buildCartItems(form, root, config, selectionResult.selections);

    setButtonLoading(button, true, form);
    document.dispatchEvent(new CustomEvent("theme:loading:start"));

    fetch(getAddUrl(), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ items }),
    })
      .then((response) => {
        if (!response.ok) {
          return readCartAddError(response).then((message) => {
            throw new Error(message || `Cart add failed: ${response.status}`);
          });
        }
        return response.json();
      })
      .then(() => {
        setButtonLoading(button, false, form);
        if (shouldRedirectToCartAfterAdd()) {
          clearRedirectToCartAfterAdd();
          notifyLoadingEnd();
          redirectToCart();
          return;
        }

        notifyAdded(form, button, getBundleQuantity(form));
      })
      .catch((error) => {
        console.error("Customized bundle: add to cart failed", error);
        clearRedirectToCartAfterAdd();
        setFormError(root, error && error.message);
        focusFormError(root);
        closeUploadLogoDialog();
        setButtonLoading(button, false, form);
        notifyLoadingEnd();
      });
  }

  function syncSlotAfterComponentChange(slot) {
    clearFormError(slot.closest(BUNDLE_ROOT_SELECTOR));
    commitSlotQuantity(slot);
    clearSlotError(slot);
    updateSummary(slot.closest(BUNDLE_ROOT_SELECTOR));
    updateDisplayedPrices(slot.closest(BUNDLE_ROOT_SELECTOR));
  }

  function getMatrixSlotConfig(root, slot) {
    const config = parseConfig(root);
    if (!config) return null;
    return config.slots.find((candidate) => String(candidate.id) === String(slot.dataset.slotId)) || null;
  }

  function uniqueValues(values) {
    return values.filter((value, index) => value && values.indexOf(value) === index);
  }

  function setMatrixOptions(select, values, placeholder) {
    const currentValue = select.value;
    select.innerHTML = "";

    const placeholderOption = document.createElement("option");
    placeholderOption.value = "";
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);

    values.forEach((value) => {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = value;
      select.appendChild(option);
    });

    select.value = values.includes(currentValue) ? currentValue : "";
    select.disabled = values.length === 0;
  }

  function resolveMatrixSlot(slot, changedIndex) {
    const root = slot.closest(BUNDLE_ROOT_SELECTOR);
    const slotConfig = getMatrixSlotConfig(root, slot);
    if (!slotConfig || !Array.isArray(slotConfig.selectors) || !Array.isArray(slotConfig.components)) return;

    const selectors = Array.from(slot.querySelectorAll(MATRIX_SELECTOR));
    const placeholder = getRootMessage(root, "placeholderText", "Choose a selection...");

    selectors.forEach((select, index) => {
      if (changedIndex != null && index > changedIndex) {
        select.value = "";
      }

      const priorSelections = selectors.slice(0, index).reduce((values, priorSelect) => {
        if (priorSelect.value) values[priorSelect.dataset.selectorId] = priorSelect.value;
        return values;
      }, {});
      const matchingComponents = slotConfig.components.filter((component) =>
        Object.entries(priorSelections).every(([selectorId, value]) =>
          component.optionValues && String(component.optionValues[selectorId]) === value
        )
      );
      const values = uniqueValues(matchingComponents.map((component) =>
        component.optionValues && String(component.optionValues[select.dataset.selectorId] || "")
      ));

      setMatrixOptions(select, values, placeholder);
      if (index > 0 && selectors[index - 1] && !selectors[index - 1].value) {
        select.disabled = true;
      }
    });

    const selectedValues = selectors.reduce((values, select) => {
      if (select.value) values[select.dataset.selectorId] = select.value;
      return values;
    }, {});
    const isComplete = selectors.length > 0 && selectors.every((select) => Boolean(select.value));
    const component = isComplete
      ? slotConfig.components.find((candidate) =>
          slotConfig.selectors.every((selector) =>
            candidate.optionValues
              && String(candidate.optionValues[selector.id]) === selectedValues[selector.id]
          )
        )
      : null;
    const componentInput = slot.querySelector(COMPONENT_SELECTOR);

    if (componentInput) {
      componentInput.value = component ? normalizeVariantId(component.variantId) : "";
      componentInput.dataset.componentTitle = component ? cleanComponentTitle(component.title || "") : "";
      componentInput.dataset.componentPrice = component ? String(component.price || "0.00") : "0.00";
      componentInput.dataset.componentPriceCents = component
        ? String(readMoneyCents(component.price, 0))
        : "0";
    }

    syncSlotAfterComponentChange(slot);
  }

  function initializeMatrixSlots(root) {
    root.querySelectorAll(`${SLOT_SELECTOR}[data-customized-bundle-matrix="true"]`).forEach((slot) => {
      resolveMatrixSlot(slot, null);
    });
  }

  function updateSummary(root) {
    if (!root) return;

    const scope = root.closest("form") || root;
    const summary = scope.querySelector("[data-customized-bundle-summary]");
    const list = scope.querySelector("[data-customized-bundle-summary-list]");
    if (!summary || !list) return;
    if (root.dataset.customizedBundleShowSummary === "false") {
      list.innerHTML = "";
      summary.hidden = true;
      return;
    }

    const rows = Array.from(root.querySelectorAll(SLOT_SELECTOR))
      .map((slot) => {
        const component = getSelectedComponent(slot);
        const quantity = getSlotQuantity(slot);
        if (!component || quantity <= 0) return "";
        const includeHiddenFixedSummary = root.dataset.customizedBundleShowHiddenFixedSummary === "true";
        if (slot.dataset.customizedBundleSummaryHidden === "true" && !(includeHiddenFixedSummary && slot.classList.contains("customized-bundle__slot--hidden-fixed"))) return "";

        return [
          '<div class="customized-bundle__summary-group">',
          `<p class="customized-bundle__summary-label">${escapeHtml(slot.dataset.slotLabel || "")}:</p>`,
          `<p class="customized-bundle__summary-value">${quantity} x ${escapeHtml(component.title || "")}</p>`,
          "</div>",
        ].join("");
      })
      .filter(Boolean);

    list.innerHTML = rows.join("");
    summary.hidden = rows.length === 0;
  }

  function positionSummary(root) {
    if (!root || root.dataset.customizedBundleSummaryPlacement !== "after-atc") return;

    const form = root.closest('form[action*="/cart/add"]');
    if (!form) return;

    const summary = form.querySelector("[data-customized-bundle-summary]");
    const buyButtons = form.querySelector(".product-form__buy-buttons");
    if (!summary || !buyButtons) return;

    if (buyButtons.nextElementSibling !== summary) {
      buyButtons.insertAdjacentElement("afterend", summary);
    }
  }

  function positionFormError(root) {
    const form = root && root.closest('form[action*="/cart/add"]');
    if (!form) return;

    const error = form.querySelector("[data-customized-bundle-form-error]");
    const buyButtons = form.querySelector(".product-form__buy-buttons");
    if (!error || !buyButtons) return;

    if (buyButtons.nextElementSibling !== error) {
      buyButtons.insertAdjacentElement("afterend", error);
    }
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function initializeRoot(root) {
    if (!root || root.dataset.customizedBundleInitialized === "true") return;
    root.dataset.customizedBundleInitialized = "true";
    positionSummary(root);
    positionFormError(root);
    initializeMatrixSlots(root);

    root.querySelectorAll(SLOT_SELECTOR).forEach((slot) => {
      syncSlotAfterComponentChange(slot);
      updateQuantityButtons(slot);
    });

    updateSummary(root);
    updateDisplayedPrices(root);
  }

  function initializeAll() {
    document.querySelectorAll(BUNDLE_ROOT_SELECTOR).forEach(initializeRoot);
  }

  document.addEventListener("change", (event) => {
    const matrixSelector = event.target.closest(MATRIX_SELECTOR);
    if (matrixSelector) {
      const slot = matrixSelector.closest(SLOT_SELECTOR);
      if (!slot) return;
      resolveMatrixSlot(slot, readInteger(matrixSelector.dataset.selectorIndex, 0));
      return;
    }

    const componentInput = event.target.closest(COMPONENT_SELECTOR);
    if (!componentInput) return;

    const slot = componentInput.closest(SLOT_SELECTOR);
    if (!slot) return;
    syncSlotAfterComponentChange(slot);
  });

  document.addEventListener("click", (event) => {
    const decrease = event.target.closest("[data-customized-bundle-quantity-decrease]");
    const increase = event.target.closest("[data-customized-bundle-quantity-increase]");
    if (!decrease && !increase) return;

    const slot = event.target.closest(SLOT_SELECTOR);
    if (!slot) return;

    event.preventDefault();
    const delta = increase ? 1 : -1;
    setSlotQuantity(slot, getSlotQuantity(slot) + delta);
    clearSlotError(slot);
    updateSummary(slot.closest(BUNDLE_ROOT_SELECTOR));
    updateDisplayedPrices(slot.closest(BUNDLE_ROOT_SELECTOR));
  });

  document.addEventListener("input", (event) => {
    const quantityInput = event.target.closest(QUANTITY_SELECTOR);
    if (!quantityInput) {
      const productQuantityInput = event.target.closest('form[action*="/cart/add"] [name="quantity"]');
      if (productQuantityInput) {
        const form = productQuantityInput.closest('form[action*="/cart/add"]');
        updateDisplayedPrices(form && form.querySelector(BUNDLE_ROOT_SELECTOR));
      }
      return;
    }

    const slot = quantityInput.closest(SLOT_SELECTOR);
    if (!slot) return;

    const sanitizedValue = sanitizeQuantityInput(quantityInput.value);
    if (quantityInput.value !== sanitizedValue) {
      quantityInput.value = sanitizedValue;
    }

    updateQuantityButtons(slot);
    clearSlotError(slot);
    updateSummary(slot.closest(BUNDLE_ROOT_SELECTOR));
    updateDisplayedPrices(slot.closest(BUNDLE_ROOT_SELECTOR));
  });

  document.addEventListener("focusout", (event) => {
    const quantityInput = event.target.closest(QUANTITY_SELECTOR);
    if (!quantityInput) return;

    const slot = quantityInput.closest(SLOT_SELECTOR);
    if (!slot) return;

    commitSlotQuantity(slot);
    clearSlotError(slot);
    updateSummary(slot.closest(BUNDLE_ROOT_SELECTOR));
    updateDisplayedPrices(slot.closest(BUNDLE_ROOT_SELECTOR));
  });

  document.addEventListener("click", (event) => {
    if (!event.target.closest('sticky-atc [data-action="decrease-picker-quantity"], sticky-atc [data-action="increase-picker-quantity"], .sticky-atc [data-action="decrease-picker-quantity"], .sticky-atc [data-action="increase-picker-quantity"]')) {
      return;
    }

    window.setTimeout(() => {
      document.querySelectorAll(BUNDLE_ROOT_SELECTOR).forEach(updateDisplayedPrices);
    }, 0);
  });

  document.addEventListener("click", (event) => {
    const button = event.target.closest(ADD_TO_CART_SELECTOR);
    if (!button) return;

    const form = button.closest('form[action*="/cart/add"]');
    if (!form || !form.querySelector(BUNDLE_ROOT_SELECTOR)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    submitCustomizedBundle(form, button);
  }, true);

  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    if (!form.querySelector(BUNDLE_ROOT_SELECTOR)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    submitCustomizedBundle(form, form.querySelector(ADD_TO_CART_SELECTOR));
  }, true);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeAll);
  } else {
    initializeAll();
  }

  window.EzupCustomizedBundleNative = {
    initialize: initializeAll,
    validateForm: validateCustomizedBundleForm,
    validateFormForDialog: validateCustomizedBundleFormForDialog,
  };
})();
